import type { ModuleFederationRuntimePlugin } from '@module-federation/runtime';

/**
 * Runtime URL override for the `@module-federation/vite` remotes.
 *
 * In the host federation config each remote's `entry` is set to its override
 * KEY (`MF_REMOTE_<NS>_URL`) rather than a URL. This plugin resolves that key at
 * runtime in the `beforeRequest` hook — which runs before the runtime fetches
 * the manifest — replicating the legacy `@originjs` `getMFRemotePromise`
 * behaviour with the same key names and 3-tier precedence, so ops/QA overrides
 * keep working unchanged:
 *   1. `localStorage['f11.<key>']`
 *   2. `window.appConfig.<key>`
 *   3. `import.meta.env['VITE_' + <key>]` (build-time fallback)
 *
 * Because every remote's `entry` is a `MF_REMOTE_*_URL` key, no per-remote
 * config (allow-list) is needed: the plugin treats any `entry` that is not
 * already an http(s) URL as a key to resolve.
 *
 * NOTE: the dynamic `import.meta.env['VITE_' + key]` access is reliable here
 * because the key is always prefixed with `VITE_` — Vite exposes all `VITE_*`
 * vars on the client `import.meta.env` object.
 */

/**
 * Matches the override key `MF_REMOTE_<NS>_URL`. By the time `beforeRequest`
 * runs, the MF runtime has already absolutized the bare `entry` against the
 * host origin (e.g. `http://localhost:9999/MF_REMOTE_MEDIAPLANNING_URL`), so we
 * match the key anywhere in the value rather than anchoring it.
 */
const ENTRY_KEY_PATTERN = /MF_REMOTE_[A-Z0-9_]+_URL/;

/**
 * `@originjs` consumed a `remoteEntry.js`; `@module-federation/vite` consumes a
 * `mf-manifest.json`. Existing env/appConfig/localStorage overrides still point
 * at the legacy `.../assets/remoteEntry.js`, so rewrite that form to the sibling
 * `.../mf-manifest.json`. This keeps the current env files / pipelines working
 * until they are migrated to emit the manifest URL directly; a value that is
 * already a manifest (or any other path) is returned unchanged.
 */
const normalizeToManifestUrl = (url: string): string =>
  url.replace(/\/(?:assets\/)?remoteEntry\.js(\?.*)?$/, '/mf-manifest.json$1');

const resolveRemoteEntry = (key: string): string | undefined => {
  const override = localStorage.getItem(`f11.${key}`);
  if (override) {
    console.warn(`${key} is overridden by localStorage:`, override);
    return normalizeToManifestUrl(override);
  }

  const runtimeRemoteURL = (
    window?.appConfig as Record<string, string | undefined> | undefined
  )?.[key];
  if (runtimeRemoteURL) {
    // biome-ignore lint/suspicious/noConsole: allowed
    console.log(
      `%c ${key} remote : \n${runtimeRemoteURL}`,
      'background: #5d7535; color: #fff; padding: 5px;',
    );
    return normalizeToManifestUrl(runtimeRemoteURL);
  }

  const envFallback = (import.meta.env as Record<string, string | undefined>)[
    `VITE_${key}`
  ];
  console.warn(`${key} not provided, using env as fallback:`, envFallback);
  return envFallback ? normalizeToManifestUrl(envFallback) : envFallback;
};

const mfRuntimeRemoteUrlPlugin = (): ModuleFederationRuntimePlugin => ({
  name: 'adm-mf-runtime-remote-url-plugin',
  beforeRequest(args) {
    const remotes = args.options.remotes ?? [];
    for (const remote of remotes) {
      if (!('entry' in remote)) {
        continue;
      }

      // The runtime has already absolutized the bare key against the host
      // origin; recover the `MF_REMOTE_*_URL` key from the value. Anything that
      // doesn't contain a key is a real URL and is left untouched.
      const key = remote.entry.match(ENTRY_KEY_PATTERN)?.[0];
      if (!key) {
        continue;
      }

      const resolved = resolveRemoteEntry(key);
      if (resolved) {
        remote.entry = resolved;
      }
    }

    return args;
  },
  errorLoadRemote(args) {
    throw args.error;
  },
});

export default mfRuntimeRemoteUrlPlugin;
